import { Drawer } from 'antd';
import { useEffect, useState } from 'react';
import { Image } from 'antd';
import moment from 'moment';

import RevenueDetailComponent from './RevenueDetailComponent';
import RevenueContractListComponent from './RevenueContractListComponent';
import RevenueIncomeListComponent from './RevenueIncomeListComponent';
import PDFViewModal2 from '../../components/expenses/PDFViewModal2';
import {
  createIncome,
  deleteIncome,
  downloadFile,
  updateIncome,
} from '../../api/constlink';
import { getIncomeFileAPIPath, initializeUploadProps } from '../../util/utils';
import IncomeCreateModal from '../../components/revenues/IncomeCreateModal';
import IncomeUpdateModal from '../../components/revenues/IncomeUpdateModal';

interface RevenueDetailDrawerProps {
  enterpriseId: string;
  visibleDetailDrawer: boolean;
  projectRevenue: any;
  onClose: () => void;
  updateRevenue: (incomes: any[]) => void;
}

function RevenueDetailDrawer({
  enterpriseId,
  visibleDetailDrawer,
  projectRevenue,
  onClose,
  updateRevenue,
}: RevenueDetailDrawerProps) {
  const [isOpenCreateIncomModal, setIsOpenCreateIncomModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [imageData, setImageData] = useState<any>();
  const [previewFileType, setPreviewFileType] = useState<string>('');
  const [uploadProps, setUploadProps] = useState<any>();
  const [selectedIncome, setSelectedIncome] = useState<any>();

  useEffect(() => {
    if (projectRevenue === null || projectRevenue === undefined) return;
    if (isOpenCreateIncomModal === true || selectedIncome !== null) {
      (async () => {
        setUploadProps(
          await initializeUploadProps(
            getIncomeFileAPIPath(enterpriseId, projectRevenue.id),
          ),
        );
      })();
    }
  }, [enterpriseId, projectRevenue, isOpenCreateIncomModal, selectedIncome]);

  const openImagePreview = (file: any) => {
    setImageData(file);
    setVisible(true);
    if (file.name.split('.').pop() === 'pdf') {
      setPreviewFileType('pdf');
    } else if (
      file.name.split('.').pop() === 'jpeg' ||
      file.name.split('.').pop() === 'jpg' ||
      file.name.split('.').pop() === 'png'
    ) {
      setPreviewFileType('image');
    } else {
      setPreviewFileType('');
    }
  };

  const onDeleteIncome = async (incomeId: number) => {
    const res = await deleteIncome({
      enterpriseId,
      projectId: projectRevenue.id,
      incomeId,
    });

    if (res.success) {
      updateRevenue(
        projectRevenue.incomes.filter((income: any) => income.id !== incomeId),
      );
    } else {
      console.error('deleteIncome error', res);
    }

    setSelectedIncome(null);
  };

  const onUpdateIncome = async (formData: any) => {
    if (formData.file) {
      for (const _file of formData.file) {
        if ('response' in _file) {
          _file.path = _file.response.path;
          _file.mimeType = _file.response.mimeType;
        }
      }
    }

    const response = await updateIncome({
      enterpriseId,
      projectId: projectRevenue.id,
      incomeId: selectedIncome.id,
      data: {
        ...formData,
        depositDate: moment(formData.depositDate).format('YYYY-MM-DD'),
      },
    });

    for (let i = 0; i < projectRevenue.incomes.length; i++) {
      if (projectRevenue.incomes[i].id === response.data.id) {
        projectRevenue.incomes[i] = { ...response.data };
      }
    }

    setSelectedIncome(null);
  };

  const onCreateIncome = async (formData: any) => {
    if (formData.file) {
      for (const _file of formData.file) {
        if ('response' in _file) {
          _file.path = _file.response.path;
          _file.mimeType = _file.response.mimeType;
        }
      }
    }

    const response = await createIncome({
      enterpriseId,
      projectId: projectRevenue.id,
      data: {
        ...formData,
        depositDate: moment(formData.depositDate).format('YYYY-MM-DD'),
      },
    });
    projectRevenue.incomes.push(response.data);

    setIsOpenCreateIncomModal(false);
  };

  const openUpdateIncomeModal = (income: any) => {
    setSelectedIncome(income);
  };

  return (
    <>
      <Drawer
        width={1000}
        placement="right"
        closable={false}
        onClose={onClose}
        open={visibleDetailDrawer}
      >
        <>
          {projectRevenue && (
            <RevenueDetailComponent projectRevenue={projectRevenue} />
          )}
          {projectRevenue && (
            <RevenueIncomeListComponent
              incomeList={projectRevenue.incomes}
              openCreateIncomeModal={() => setIsOpenCreateIncomModal(true)}
              openUpdateIncomeModal={openUpdateIncomeModal}
              onDeleteIncome={onDeleteIncome}
              downloadFile={async (attachFileId: string, fileName: string) => {
                await downloadFile(
                  getIncomeFileAPIPath(enterpriseId, projectRevenue.id),
                  attachFileId,
                  fileName,
                );
              }}
              openImagePreview={openImagePreview}
            />
          )}
          {projectRevenue && (
            <RevenueContractListComponent projectRevenue={projectRevenue} />
          )}
        </>
      </Drawer>
      {imageData && previewFileType === 'image' && (
        <Image
          width={200}
          style={{ display: 'none' }}
          preview={{
            visible,
            src: imageData?.url,
            onVisibleChange: (value) => {
              setVisible(value);
              setImageData(null);
              setPreviewFileType('');
            },
          }}
        />
      )}
      {imageData && previewFileType === 'pdf' && (
        <PDFViewModal2
          visible={visible}
          url={imageData.url}
          onCancel={() => {
            setVisible(false);
            setImageData(null);
            setPreviewFileType('');
          }}
          onDownload={() =>
            downloadFile(
              getIncomeFileAPIPath(enterpriseId, projectRevenue.id),
              imageData.id,
              imageData.name,
            )
          }
        />
      )}

      {uploadProps && (
        <IncomeCreateModal
          isOpen={isOpenCreateIncomModal}
          onCreate={onCreateIncome}
          onClose={() => setIsOpenCreateIncomModal(false)}
          uploadProps={uploadProps}
        />
      )}

      {selectedIncome && uploadProps && (
        <IncomeUpdateModal
          isOpen={!!selectedIncome}
          income={selectedIncome}
          onCreate={onUpdateIncome}
          onClose={() => setSelectedIncome(null)}
          uploadProps={uploadProps}
        />
      )}
    </>
  );
}

export default RevenueDetailDrawer;
